import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Plus, X, AlertCircle, Clock,
  ToggleLeft, ToggleRight, ChevronDown, Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { flightSyncAPI } from '../../services/api';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';

// Used only when a destination doesn't resolve to a language — with every
// country now mapped, this mostly covers unknown airport/country codes.
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English only (no second language)' },
  { value: 'zh-CN', label: 'Mandarin (zh-CN)' },
  { value: 'hi-IN', label: 'Hindi (hi-IN)' },
  { value: 'ms-MY', label: 'Malay (ms-MY)' },
  { value: 'ta-IN', label: 'Tamil (ta-IN)' },
];

const LOOKAHEAD_OPTIONS = [1, 2, 4, 6];
const POST_LANDING_OPTIONS = [1, 2, 4, 6];

export default function AdminFlightSyncPage() {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [newAirlineCode, setNewAirlineCode] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const res = await flightSyncAPI.getStatus();
      const data = res.data.data;
      setStatus(data);
      if (!config) {
        setConfig({ ...data.config });
      }
    } catch (err) {
      if (!err._toasted) toast.error('Failed to load sync status');
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    loadStatus();
  }, []);  // Run once on mount; loadStatus handles config guard

  function formatLastSync(iso) {
    if (!iso) return 'Never';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  async function handleSyncNow() {
    setSyncing(true);
    try {
      const res = await flightSyncAPI.runSync();
      const result = res.data.data;
      await loadStatus();
      const beltAnnounced = result.beltCreated.length + result.beltChanged.length;
      toast.success(
        `Sync complete — Created: ${result.created.length} | Changed: ${result.gateChanged.length} | ` +
        `Belt announced: ${beltAnnounced} | Errors: ${result.errors.length}`
      );
    } catch (err) {
      if (!err._toasted) toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  async function saveConfig(patch) {
    const updated = { ...config, ...patch };
    setConfig(updated);
    setSavingConfig(true);
    try {
      await flightSyncAPI.updateConfig(updated);
      toast.success('Config saved');
    } catch (err) {
      if (!err._toasted) toast.error('Failed to save config');
      // Reload to get back to correct state
      await loadStatus();
    } finally {
      setSavingConfig(false);
    }
  }

  function addAirlineCode() {
    const code = newAirlineCode.trim().toUpperCase();
    if (!code) return;
    if (config.airlineCodes.includes(code)) {
      toast.error(`${code} is already in the list`);
      return;
    }
    setNewAirlineCode('');
    saveConfig({ airlineCodes: [...config.airlineCodes, code] });
  }

  function removeAirlineCode(code) {
    saveConfig({ airlineCodes: config.airlineCodes.filter(c => c !== code) });
  }

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const lastResult = status?.lastSyncResult;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      <PageHeader
        title="Flight Sync"
        subtitle="Auto-sync FlightMS departures into gate sessions, and arrivals into belt assignments"
        actions={
          <Button variant="primary" icon={RefreshCw} loading={syncing} onClick={handleSyncNow}>
            {syncing ? 'Syncing…' : 'Sync Now'}
          </Button>
        }
      />

      {/* Sync status bar */}
      <Card className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock size={14} className="text-gray-400" />
          <span>Last sync: <span className="font-medium text-gray-800">{formatLastSync(status?.lastSyncAt)}</span></span>
        </div>
        {status?.isSyncing && (
          <span className="flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
            <Loader2 size={12} className="animate-spin" /> Running…
          </span>
        )}
        {lastResult && (
          <div className="flex items-center gap-2 flex-wrap ml-auto">
            <ResultBadge label="Created" value={lastResult.created.length} tone="success" />
            <ResultBadge label="Updated" value={lastResult.updated.length} tone="info" />
            <ResultBadge label="Gate changed" value={lastResult.gateChanged.length} tone="yellow" />
            <ResultBadge label="Locked" value={lastResult.gateLocked.length} tone="orange" />
            <span className="w-px h-4 bg-gray-200" />
            <ResultBadge label="Belt assigned" value={lastResult.beltCreated.length} tone="purple" />
            <ResultBadge label="Belt changed" value={lastResult.beltChanged.length} tone="yellow" />
            <ResultBadge label="Belt pending" value={lastResult.beltPending.length} tone="neutral" />
            <ResultBadge label="Errors" value={lastResult.errors.length} tone="danger" />
          </div>
        )}
      </Card>

      {/* Error list */}
      {lastResult?.errors?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={16} className="text-red-500" />
            <span className="font-semibold text-red-700 text-sm">Sync errors ({lastResult.errors.length})</span>
          </div>
          <ul className="space-y-1">
            {lastResult.errors.map((err, i) => (
              <li key={i} className="text-xs text-red-600 font-mono bg-red-100 rounded px-2 py-1">{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Config card */}
      <Card className="space-y-5">
        <h2 className="font-bold text-gray-900">Sync Configuration</h2>

        {/* Auto-sync toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-800">Auto-sync enabled</p>
            <p className="text-xs text-gray-500">Runs every 5 minutes automatically</p>
          </div>
          <button
            onClick={() => saveConfig({ autoSyncEnabled: !config.autoSyncEnabled })}
            disabled={savingConfig}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
          >
            {config.autoSyncEnabled
              ? <ToggleRight size={32} className="text-blue-600" />
              : <ToggleLeft size={32} />}
          </button>
        </div>

        <hr className="border-gray-100" />

        {/* Lookahead windows — departures (gate sessions) and arrivals (belts) separately */}
        <div>
          <label className="block text-sm font-medium text-gray-800 mb-2">
            Departure lookahead
          </label>
          <div className="flex gap-2 flex-wrap">
            {LOOKAHEAD_OPTIONS.map(h => (
              <button
                key={h}
                onClick={() => saveConfig({ departureLookaheadHours: h })}
                disabled={savingConfig}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${
                  config.departureLookaheadHours === h
                    ? 'bg-blue-700 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {h}h
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1.5">
            Create gate sessions for flights departing within this window — the supervisor/manager dashboard shows the next 4h, so keep this at 4h or more
          </p>
        </div>

        <hr className="border-gray-100" />

        <div>
          <label className="block text-sm font-medium text-gray-800 mb-2">
            Arrival / belt lookahead
          </label>
          <div className="flex gap-2 flex-wrap">
            {LOOKAHEAD_OPTIONS.map(h => (
              <button
                key={h}
                onClick={() => saveConfig({ lookaheadHours: h })}
                disabled={savingConfig}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${
                  config.lookaheadHours === h
                    ? 'bg-blue-700 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {h}h
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1.5">
            Sync arriving flights (belt announcements) landing within this window
          </p>
        </div>

        <hr className="border-gray-100" />

        {/* Secondary language */}
        <div>
          <label className="block text-sm font-medium text-gray-800 mb-2">
            Default secondary language
          </label>
          <select
            value={config.defaultSecondaryLanguage}
            onChange={e => saveConfig({ defaultSecondaryLanguage: e.target.value })}
            disabled={savingConfig}
            className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {LANGUAGE_OPTIONS.map(l => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </div>

        <hr className="border-gray-100" />

        {/* Airline codes */}
        <div>
          <label className="block text-sm font-medium text-gray-800 mb-2">
            Airline codes to sync
          </label>

          {/* Chip list */}
          <div className="flex flex-wrap gap-2 mb-3">
            {config.airlineCodes.map(code => (
              <span
                key={code}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-800 font-mono font-semibold text-sm rounded-full"
              >
                {code}
                <button
                  onClick={() => removeAirlineCode(code)}
                  disabled={savingConfig}
                  className="text-blue-400 hover:text-blue-700 disabled:opacity-50 transition-colors"
                >
                  <X size={12} />
                </button>
              </span>
            ))}
            {config.airlineCodes.length === 0 && (
              <span className="text-sm text-gray-400">No airline codes configured</span>
            )}
          </div>

          {/* Add code form */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newAirlineCode}
              onChange={e => setNewAirlineCode(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addAirlineCode())}
              placeholder="e.g. SQ"
              maxLength={4}
              className="flex-1 border border-gray-300 rounded-xl px-3 py-2.5 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Button variant="primary" icon={Plus} disabled={!newAirlineCode.trim() || savingConfig} onClick={addAirlineCode}>
              Add
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-1.5">
            2-letter IATA airline code (e.g. SQ = Singapore Airlines)
          </p>
        </div>
      </Card>

      {/* Arrivals / Belt Assignments config card */}
      <Card className="space-y-5">
        <div>
          <h2 className="font-bold text-gray-900">Arrivals (Belt Assignments)</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Same FlightMS sync also creates belt assignments for arrivals and fires belt-change /
            first-bag / disembarkation announcements — configured here, worded on Announcement Rules.
          </p>
        </div>

        {/* Arrival sync toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-800">Arrival sync enabled</p>
            <p className="text-xs text-gray-500">Creates/updates belt assignments and fires baggage announcements</p>
          </div>
          <button
            onClick={() => saveConfig({ arrivalSyncEnabled: !config.arrivalSyncEnabled })}
            disabled={savingConfig}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors"
          >
            {config.arrivalSyncEnabled
              ? <ToggleRight size={32} className="text-blue-600" />
              : <ToggleLeft size={32} />}
          </button>
        </div>

        <hr className="border-gray-100" />

        {/* Post-landing window */}
        <div>
          <label className="block text-sm font-medium text-gray-800 mb-2">
            Post-landing window
          </label>
          <div className="flex gap-2 flex-wrap">
            {POST_LANDING_OPTIONS.map(h => (
              <button
                key={h}
                onClick={() => saveConfig({ arrivalPostLandingHours: h })}
                disabled={savingConfig || !config.arrivalSyncEnabled}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${
                  config.arrivalPostLandingHours === h
                    ? 'bg-blue-700 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {h}h
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1.5">
            Keep reacting to belt changes / first-bag / disembarkation for this long after scheduled arrival
          </p>
        </div>

        <hr className="border-gray-100" />

        {/* Belt change cap */}
        <div>
          <label className="block text-sm font-medium text-gray-800 mb-2">
            Belt change announcement cap
          </label>
          <input
            type="number"
            value={config.beltChangeMaxAnnouncements}
            onChange={e => saveConfig({ beltChangeMaxAnnouncements: parseInt(e.target.value) || 1 })}
            disabled={savingConfig || !config.arrivalSyncEnabled}
            min={1}
            max={10}
            className="w-24 border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          />
          <p className="text-xs text-gray-400 mt-1.5">
            Maximum re-announcements per flight if the belt keeps changing — further changes update
            the record silently without a new PA, mirroring the gate-change cap
          </p>
        </div>
      </Card>

      {/* Last sync details */}
      {lastResult && (
        <Card>
          <h2 className="font-bold text-gray-900 mb-3">Last Sync Details</h2>
          <div className="text-xs text-gray-500 mb-3">
            {lastResult.source === 'manual' ? 'Manual sync' : 'Auto sync'} at{' '}
            {new Date(lastResult.syncedAt).toLocaleString()}
          </div>

          {lastResult.created.length > 0 && (
            <DetailSection
              title="Created sessions"
              color="green"
              items={lastResult.created.map(i => `${i.flightNumber} → Gate ${i.gate}`)}
            />
          )}
          {lastResult.gateChanged.length > 0 && (
            <DetailSection
              title="Gate changes applied"
              color="yellow"
              items={lastResult.gateChanged.map(i => `${i.flightNumber}: Gate ${i.oldGate} → ${i.newGate}`)}
            />
          )}
          {lastResult.gateLocked.length > 0 && (
            <DetailSection
              title="Gate conflicts (locked by operator)"
              color="orange"
              items={lastResult.gateLocked.map(i => `${i.flightNumber}: locked at ${i.lockedGate}, FlightMS says ${i.flightmsGate}`)}
            />
          )}
          {lastResult.unchanged.length > 0 && (
            <p className="text-xs text-gray-400 mb-3">{lastResult.unchanged.length} session(s) unchanged</p>
          )}

          {(lastResult.beltCreated.length > 0 || lastResult.beltChanged.length > 0 ||
            lastResult.beltCapped.length > 0 || lastResult.beltPending.length > 0 ||
            lastResult.firstBagAnnounced.length > 0 || lastResult.disembarkDelayed.length > 0 ||
            lastResult.disembarkResumed.length > 0) && (
            <hr className="border-gray-100 my-3" />
          )}

          {lastResult.beltCreated.length > 0 && (
            <DetailSection
              title="Belt announced"
              color="indigo"
              items={lastResult.beltCreated.map(i => `${i.flightNumber} → Belt ${i.belt} (${i.zone})`)}
            />
          )}
          {lastResult.beltChanged.length > 0 && (
            <DetailSection
              title="Belt changes announced"
              color="yellow"
              items={lastResult.beltChanged.map(i => `${i.flightNumber} → Belt ${i.newBelt} (${i.zone})`)}
            />
          )}
          {lastResult.beltCapped.length > 0 && (
            <DetailSection
              title="Belt changes capped (no further PA)"
              color="orange"
              items={lastResult.beltCapped.map(i => `${i.flightNumber}: now Belt ${i.belt}`)}
            />
          )}
          {lastResult.firstBagAnnounced.length > 0 && (
            <DetailSection
              title="First bag announced"
              color="green"
              items={lastResult.firstBagAnnounced.map(i => `${i.flightNumber}`)}
            />
          )}
          {lastResult.disembarkDelayed.length > 0 && (
            <DetailSection
              title="Disembarkation delay announced"
              color="orange"
              items={lastResult.disembarkDelayed.map(i => `${i.flightNumber} (${i.elapsedMin}m since touchdown)`)}
            />
          )}
          {lastResult.disembarkResumed.length > 0 && (
            <DetailSection
              title="Disembarkation resumed announced"
              color="green"
              items={lastResult.disembarkResumed.map(i => `${i.flightNumber}`)}
            />
          )}
          {lastResult.beltPending.length > 0 && (
            <p className="text-xs text-gray-400">
              {lastResult.beltPending.length} belt assignment(s) pre-landing — tracked silently, announcement deferred until touchdown
            </p>
          )}
        </Card>
      )}
    </div>
  );
}

function ResultBadge({ label, value, tone }) {
  return <StatusBadge label={`${label}: ${value}`} tone={tone} />;
}

function DetailSection({ title, color, items }) {
  const [open, setOpen] = useState(false);
  const borderColors = {
    green: 'border-green-200 bg-green-50',
    yellow: 'border-yellow-200 bg-yellow-50',
    orange: 'border-orange-200 bg-orange-50',
    indigo: 'border-indigo-200 bg-indigo-50',
  };
  return (
    <div className={`border rounded-xl p-3 mb-2 ${borderColors[color] || 'border-gray-200 bg-gray-50'}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center justify-between w-full text-left"
      >
        <span className="text-xs font-semibold text-gray-700">{title} ({items.length})</span>
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul className="mt-2 space-y-1">
          {items.map((item, i) => (
            <li key={i} className="text-xs text-gray-600 font-mono">{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
